/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { isRecord, safeToString } from "./type.utils";

/** Plain, serialisable summary of an error, safe to pass to `console.error` or a logger. */
export interface ErrorDescription {
	message: string;
	name?: string;
	code?: string;
	cause?: string;
	causeCode?: string;
}

/**
 * Upper bound on each string field. An error thrown during an incident can carry a whole
 * HTML error page or a GraphQL response body. At incident traffic levels that inflates the
 * log volume without adding anything to act on.
 */
const MAX_FIELD_LENGTH = 500;

/**
 * Upper bound on the text the redaction patterns scan.
 *
 * A thrown value can carry a whole HTML error page, and this helper runs inside catch
 * blocks at incident traffic levels. Scanning an unbounded payload three times is work
 * nobody reads, because only `MAX_FIELD_LENGTH` characters survive to the log. The bound
 * sits well above that cap so redaction still sees more than it can ever emit.
 */
const MAX_REDACTION_INPUT = 2000;

/**
 * Credential shapes to remove before any text reaches the log.
 *
 * ReDoS rule for anyone adding a pattern here: no quantifier may nest inside another, and
 * two adjacent quantified classes must be disjoint, so the engine never has a choice about
 * which one consumed a character. One place has a deliberate exception, the `[\w-]{0,64}?`
 * key prefix below: it can overlap the literal that follows it, so the engine does have a
 * choice, but the upper bound of 64 caps the cost at a constant factor rather than letting
 * it grow with the input. Every pattern is measured linear, not assumed linear. That matters
 * because the input is whatever an upstream chose to throw, which is exactly the input an
 * attacker can influence.
 *
 * The key list is deliberately wider than `authorization`. Redaction is unconditional: it
 * does NOT require a `Bearer` or `Basic` scheme word first. Requiring one would read
 * better during an incident, because a plain-English authorization reason would survive,
 * but it would also leave every schemeless credential in the log, and an API key is
 * exactly that. A lost sentence costs less than a leaked key.
 */
const SECRET_PATTERNS: readonly [RegExp, string][] = [
	// A raw `Cookie` or `Set-Cookie` header line, which holds several pairs separated by
	// `;`. The general key/value pattern below stops at the first `;`, so it would redact
	// `sid` and leave `csrf` in the log. A cookie header is a credential from the colon to
	// the end of the line, so redact all of it. The first value character must not be a
	// quote, which sends the common serialised-JSON shape `"cookie":"sid=A"` to the pattern
	// below instead: that one ends at the closing quote and so keeps any later field in the
	// payload. The guard is narrow. `\s*` can give the whitespace back, so `cookie: "sid=A",
	// status: 500` still redacts to end of line. That is over-redaction in the safe
	// direction, and the JSON shape that matters carries no space after the colon.
	[/(^|[^\w-])((?:set-)?cookie\s*[:=]\s*)[^\n"'][^\n]*/gi, "$1$2[redacted]"],
	// A credential in a key/value pair: serialised JSON, a raw header line, or a query
	// string. The quote characters are optional, and may themselves be escaped, because an
	// error message often holds a payload that something serialised twice.
	//
	// `[\w-]{0,64}?` lets a key carry a prefix, because the prefixed names are the ones real
	// payloads use: `access_token`, `refresh_token`, `client_secret`, `sessionToken`,
	// `x_auth_token`, `proxy-authorization`. A bare `token` alternative matches none of them,
	// since the boundary before it rejects the `_` or the letter that precedes it. The prefix
	// is a bounded class rather than a list of known prefixes, so a name nobody thought of
	// still redacts. The cost is that any key ending in one of these words is redacted, so
	// `hasToken: false` loses its value. That is the same trade as the rest of this list: a
	// lost diagnostic line costs less than a leaked credential.
	//
	// The bound is 64 rather than a smaller number because this repository's own credential
	// names are long: `SITE_ONLINESERVICES_SERVICE_CLIENT_SECRET` carries 35 characters of
	// prefix, and `EPIC_BUILD_CREDENTIALS_ARTIFACTS_PASSWORD` carries 34. A bound of 24 read
	// as generous and silently passed both of them through whole. Keep the bound above the
	// longest name in the repository, and keep it bounded: an unbounded `*` is the ReDoS
	// case the docstring above rules out.
	//
	// The leading `\b` is a zero-width boundary, not a lookbehind. It keeps the prefix from
	// starting mid-word, which is what a lookbehind would do, but it parses in the Nintendo
	// Switch in-game browser. This module reaches a client chunk, and `check-browser-targets`
	// fails a build that needs Safari 16.4 for a lookbehind (MW-13776).
	//
	// Zero-width matters for more than tidiness. A boundary that CONSUMES its character
	// breaks the `g` scan wherever one credential ends exactly where the next begins: the
	// quoted-value branches eat their closing quote, so in `authorization:"A"token:"B"` there
	// is no spare character left for the second match to consume, and `B` reached the log.
	// `\b` needs no character, so both redact.
	//
	// The value has four alternatives, in order. A double-quoted run, then a single-quoted
	// run, each escape-aware so an embedded \" cannot end the match early. They are separate
	// branches rather than one `["']` class on purpose: a shared class stops at the FIRST
	// quote of either kind, so an apostrophe inside a double-quoted value ("it's-a-secret")
	// ends the match and leaks the rest. Third, an unquoted run to the next delimiter.
	// Last, a fallback for a value that opens a quote and never closes it, which is what an
	// upstream that truncated its own message leaves behind. The fallback redacts to the end
	// of the line: over-redaction, in the safe direction, on a shape that is already broken.
	[
		/\b((?:\\?["'])?[\w-]{0,64}?(?:authorization|set-cookie|cookie|x-api-key|api[-_]?key|token|password|secret)(?:\\?["'])?\s*[:=]\s*)(?:\\?"(?:[^"\\]|\\.)*\\?"|\\?'(?:[^'\\]|\\.)*\\?'|[^\n,;&}"']+|\\?["'][^\n]*)/gi,
		"$1[redacted]",
	],
	// A bearer token in free text, outside any key/value shape. The value runs to the next
	// delimiter rather than matching an allow-list of token characters: an allow-list stops
	// at the first character it does not know and leaves the rest of the token in the log.
	// The 8-character floor keeps prose readable. "the bearer of this error" must survive,
	// and no credential this codebase issues is that short.
	[/\b(bearer)\s+[^\s"',;]{8,}/gi, "$1 [redacted]"],
	// Credentials in a URL userinfo section, the shape a connection string takes.
	[/(:\/\/)[^\s:@/]+:[^\s:@/]+@/g, "$1[redacted]@"],
];

/**
 * Removes credentials, then caps the length.
 *
 * Every string this module returns passes through here, so the redaction is a property of
 * the helper rather than a rule each call site has to remember. Redaction runs before the
 * length cap: a token cut in half by the cap is still a leaked prefix.
 *
 * The input bound is applied first, which is safe in the only direction that matters.
 * Redaction can only shorten text, so text beyond `MAX_REDACTION_INPUT` can reach the
 * visible output only if the patterns removed more than 1500 characters ahead of it. In
 * that case the tail is dropped rather than shown unscanned.
 *
 * One residue that the paragraph above does not cover: a credential that straddles
 * character 2000 is cut there, and the surviving head is a fragment no pattern recognises.
 * If redaction then shrinks everything ahead of it below `MAX_FIELD_LENGTH`, that fragment
 * reaches the log. It is a leaked prefix of a few characters from a message long enough to
 * be truncated twice, so the bound stays where it is rather than growing to chase it.
 */
function redactAndCap(value: string): string {
	const bounded = value.length > MAX_REDACTION_INPUT ? value.slice(0, MAX_REDACTION_INPUT) : value;
	const redacted = SECRET_PATTERNS.reduce<string>(
		(text, [pattern, replacement]) => text.replace(pattern, replacement),
		bounded,
	);
	return redacted.length > MAX_FIELD_LENGTH ? `${redacted.slice(0, MAX_FIELD_LENGTH)}…` : redacted;
}

/** Reads `value.code` as a string, if `value` is object-like and carries one. */
function readCode(value: unknown): string | undefined {
	if (isRecord(value) && (typeof value.code === "string" || typeof value.code === "number")) {
		return redactAndCap(String(value.code));
	}
	return undefined;
}

/**
 * Reads the next error down the chain. Prefers `value.cause`, and falls back to the first
 * entry of `value.errors`, because undici reports a Happy Eyeballs failure as an
 * `AggregateError` that carries the real reasons in `errors`, not in `cause`.
 */
function readCause(value: unknown): unknown {
	if (!isRecord(value)) {
		return undefined;
	}
	if (value.cause !== undefined && value.cause !== null) {
		return value.cause;
	}
	return Array.isArray(value.errors) ? value.errors[0] : undefined;
}

/** Reads `value.message` as a string, falling back to a safe stringification. */
function messageOf(value: unknown): string {
	if (isRecord(value) && typeof value.message === "string") {
		return redactAndCap(value.message);
	}
	return redactAndCap(safeToString(value));
}

/**
 * Reads a cause's message, without serialising an unknown object into the log.
 *
 * Deliberately narrower than `messageOf`. That one is used on the thrown value, where
 * `safeToString` is the point: a bare GraphQL payload should not collapse to
 * `[object Object]`. A cause is different. A library may attach its whole request or
 * config object as `cause`, headers included, and `safeToString` would put the contents
 * in the log. `errorToLogObject` in next-common takes the same position for the same
 * field. So an object cause with no message becomes a type tag, not its contents.
 */
function causeMessageOf(value: unknown): string {
	if (!isRecord(value)) {
		// A scalar carries no structure to leak, so render it.
		return redactAndCap(safeToString(value));
	}

	if (typeof value.message === "string" && value.message !== "") {
		return redactAndCap(value.message);
	}

	// Node builds the Happy Eyeballs `AggregateError` with NO message and copies `code`
	// off `errors[0]` (`aggregateErrors`, `lib/internal/net.js`). The address and the
	// reason text only exist one level further down, so without this the cause of the
	// exact failure shape this helper was written for logs as an empty string.
	const nested = readCause(value);
	if (isRecord(nested) && typeof nested.message === "string" && nested.message !== "") {
		return redactAndCap(nested.message);
	}

	return "[object]";
}

/**
 * Summarises an unknown thrown value into a plain, serialisable object for logging.
 *
 * Node's `fetch` (undici) wraps every transport failure in a generic
 * `TypeError: fetch failed`, and hides the real reason (for example `ECONNRESET`,
 * `UND_ERR_SOCKET`, `UND_ERR_DESTROYED`, `ENOTFOUND`) on `error.cause`. A catch block
 * that logs only `error.message` throws that reason away, which makes an incident hard
 * to diagnose. This helper reads one level of cause, and the code of a second level, so
 * the real reason survives into the log. It also follows `AggregateError.errors[0]`,
 * because undici reports a Happy Eyeballs failure that way.
 *
 * This function never throws, accepts non-`Error` values, and never returns a stack trace.
 * Each field is capped at 500 characters.
 *
 * SECURITY: every field passes through `redactAndCap`, which removes the credential
 * shapes in `SECRET_PATTERNS` before the length cap applies. A cause that is an object
 * with no `message` is not serialised at all; it becomes a type tag.
 *
 * What a message still carries is worth knowing. `graphql-request`'s `ClientError` builds
 * its message by serialising the response and the request together, so it holds the
 * GraphQL query, the variables and the raw response body. It does NOT hold the request
 * headers: every construction site passes the request as the query and the variables
 * only, and the one `headers` value in the payload is the `Headers` instance from the
 * fetch response, which serialises to `{}`. The `Authorization` header that
 * `createHygraphClient` sets in `cosmos-hygraph-data` never reaches the error. An earlier
 * version of this note claimed it did, and a reviewer reasonably read that as a live
 * token leak. It is not one.
 *
 * That claim is pinned to `graphql-request@7.4.0`, the only version this repository
 * resolves. Do not trust it after an upgrade on the strength of this comment. What guards
 * it is a test in `cosmos-hygraph-data`, which is the package that actually depends on
 * `graphql-request`: it drives a real `GraphQLClient` with an `Authorization` header and
 * asserts the thrown `ClientError` message omits the token. A version that starts
 * serialising request headers fails there. This package takes no such dependency, so its
 * own test can only pin that redaction handles that payload shape, which is a different
 * property and not a version guard.
 *
 * `libraries/next-common/src/logger/logger.ts` has `errorToLogObject`, which solves the
 * cause-chain problem for callers that already depend on `next-common`. Use that one
 * there. Note that it does NOT redact: it allow-lists fields and caps their length, so a
 * credential inside a `message` survives it. This helper exists for `common-utils`
 * consumers, which cannot depend on `next-common`.
 *
 * @param error - The unknown thrown value to describe
 * @returns A plain object with `message`, `name`, `code`, `cause`, and `causeCode`
 */
export function describeError(error: unknown): ErrorDescription {
	// Every read below can throw: a property may be a getter that throws, the value may be
	// a Proxy, and a custom `toString` may throw. This function runs inside catch blocks,
	// so it must not turn a handled failure into an unhandled one.
	try {
		const name = isRecord(error) && typeof error.name === "string" ? redactAndCap(error.name) : undefined;
		const code = readCode(error);

		// Level 1: the direct cause.
		const cause = readCause(error);
		// Level 2: the cause's own cause. Read only its code, since a nested message
		// beyond one level adds noise without adding a new field to act on.
		const nestedCause = readCause(cause);

		return {
			message: messageOf(error),
			name,
			code,
			cause: cause === undefined ? undefined : causeMessageOf(cause),
			causeCode: readCode(cause) ?? readCode(nestedCause),
		};
	} catch {
		return { message: "unserialisable error" };
	}
}
